import { css } from "@emotion/css";
import { ErrorPanel } from "@octopusdeploy/design-system-components";
import type { InlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import React from "react";
import { useOptionalProjectContext } from "~/areas/projects/context/index";
import { ArgoCDApplicationsPreview } from "~/components/Actions/argocd/ApplicationsPreview/ArgoCDApplicationsPreview";
import { CreateArgoCDGateway } from "~/components/Actions/argocd/ApplicationsPreview/CreateArgoCDGateway";
import type { DeploymentConfiguration, ArgoCdInstancesAndApplications } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { isScopedToDeploymentConfiguration, useArgoCDContext } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { Skeleton } from "~/components/Skeleton/Skeleton";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import Summary from "~/components/form/Sections/Summary";
import { NoMappedApplications } from "./NoMappedApplications";
const styles = {
    skeleton: css({
        height: "24px",
    }),
};
type ContentPanelProps = {
    argoCDContext: InlineStatusQuery<ArgoCdInstancesAndApplications, null>;
    project: ProjectResource;
    deploymentConfiguration?: DeploymentConfiguration;
};
const ContentPanel = (props: ContentPanelProps) => {
    const { argoCDContext, project, deploymentConfiguration } = props;
    if (argoCDContext.isLoading && !argoCDContext.result) {
        return (<div className={styles.skeleton}>
                <Skeleton shape={"Rounded"} borderRadius={"small"}/>
            </div>);
    }
    if (argoCDContext.error) {
        const errorInfo = createErrorInfoFromError(argoCDContext.error);
        return <ErrorPanel error={errorInfo}/>;
    }
    if (argoCDContext.result) {
        if (argoCDContext.result.instances.Resources.length === 0) {
            return <CreateArgoCDGateway refetch={props.argoCDContext.refetch} spaceId={props.project.SpaceId}/>;
        }
        const projectMappedApps = argoCDContext.result.applications.filter((application) => isScopedToDeploymentConfiguration(application, project.Id, deploymentConfiguration));
        if (projectMappedApps.length === 0) {
            return <NoMappedApplications refetch={argoCDContext.refetch} projectSlug={project.Slug}/>;
        }
        else {
            return <ArgoCDApplicationsPreview applications={projectMappedApps} project={project} deploymentConfiguration={deploymentConfiguration}/>;
        }
    }
    return <ErrorPanel error={{ message: "Failed to handle Argo instances and applications, deployment preview is unavailable.", details: [] }}/>;
};
type ArgoCDApplicationsPreviewFormSectionProps = {
    expandedByDefault?: boolean;
    deploymentConfiguration?: DeploymentConfiguration;
};
export const ArgoCDApplicationsPreviewFormSection = (props: ArgoCDApplicationsPreviewFormSectionProps) => {
    const { expandedByDefault, deploymentConfiguration } = props;
    const projectContext = useOptionalProjectContext();
    const argoCDContext = useArgoCDContext(projectContext?.state.model.Id);
    if (!projectContext) {
        return <></>;
    }
    return (<ExpandableFormSection errorKey="Octopus.Action.ArgoCD.ApplicationsPreview" title="Argo CD Applications" help="Preview which Argo CD applications this step will update with your current configuration." summary={Summary.summary("Preview which Argo CD applications this step will update with your current configuration.")} isExpandedByDefault={expandedByDefault}>
            <ContentPanel project={projectContext.state.model} deploymentConfiguration={deploymentConfiguration} argoCDContext={argoCDContext}/>
        </ExpandableFormSection>);
};
